import * as THREE from "three";

const SURFACE_VERT = /* glsl */ `
varying vec2 vUv;
varying vec3 vN;
varying vec3 vV;
void main() {
  vUv = uv;
  vN = normalize(mat3(modelMatrix) * normal);
  vec4 wp = modelMatrix * vec4(position, 1.0);
  vV = normalize(cameraPosition - wp.xyz);
  gl_Position = projectionMatrix * viewMatrix * wp;
}`;

const SURFACE_FRAG = /* glsl */ `
precision highp float;
uniform sampler2D uDay;
uniform sampler2D uNight;
uniform vec3 uSun;
uniform float uGlobal;
varying vec2 vUv;
varying vec3 vN;
varying vec3 vV;
void main() {
  vec3 n = normalize(vN);
  float ndl = dot(n, uSun);
  vec3 day = texture2D(uDay, vUv).rgb;
  vec3 night = texture2D(uNight, vUv).rgb;
  float dayK = smoothstep(-0.12, 0.3, ndl);
  vec3 lit = day * (0.52 + 1.0 * max(ndl, 0.0));
  vec3 col = mix(night * vec3(1.0, 0.82, 0.58) * 1.35, lit, dayK);
  col += vec3(0.10, 0.17, 0.32) * dayK;
  float fres = pow(1.0 - max(dot(n, normalize(vV)), 0.0), 2.4);
  col += vec3(0.36, 0.58, 1.0) * fres * (0.22 + 0.5 * dayK);
  gl_FragColor = vec4(col * uGlobal, 1.0);
}`;

const CLOUD_FRAG = /* glsl */ `
precision highp float;
uniform sampler2D uClouds;
uniform vec3 uSun;
uniform float uGlobal;
varying vec2 vUv;
varying vec3 vN;
varying vec3 vV;
void main() {
  float a = texture2D(uClouds, vUv).r;
  if (a < 0.02) discard;
  vec3 n = normalize(vN);
  float ndl = max(dot(n, uSun), 0.0);
  vec3 col = vec3(0.92, 0.95, 1.0) * (0.28 + 0.85 * ndl);
  gl_FragColor = vec4(col, a * 0.82 * uGlobal);
}`;

const ATMO_VERT = /* glsl */ `
varying vec3 vN;
varying vec3 vV;
void main() {
  vN = normalize(mat3(modelMatrix) * normal);
  vec4 wp = modelMatrix * vec4(position, 1.0);
  vV = normalize(cameraPosition - wp.xyz);
  gl_Position = projectionMatrix * viewMatrix * wp;
}`;

const ATMO_FRAG = /* glsl */ `
precision highp float;
uniform vec3 uSun;
uniform float uGlobal;
varying vec3 vN;
varying vec3 vV;
void main() {
  vec3 n = normalize(vN);
  float rim = pow(clamp(0.68 - dot(n, normalize(vV)), 0.0, 1.0), 3.2);
  float sunSide = 0.45 + 0.55 * max(dot(-n, uSun), 0.0);
  gl_FragColor = vec4(vec3(0.36, 0.6, 1.0) * rim * sunSide * 1.4 * uGlobal, 1.0);
}`;

export function createEarth() {
  const group = new THREE.Group();

  const surfMat = new THREE.ShaderMaterial({
    vertexShader: SURFACE_VERT,
    fragmentShader: SURFACE_FRAG,
    uniforms: {
      uDay: { value: null },
      uNight: { value: null },
      uSun: { value: new THREE.Vector3(0.6, 0.22, 0.62).normalize() },
      uGlobal: { value: 1 },
    },
  });
  const surface = new THREE.Mesh(new THREE.SphereGeometry(13, 96, 96), surfMat);
  surface.renderOrder = 0;

  const cloudMat = new THREE.ShaderMaterial({
    vertexShader: SURFACE_VERT,
    fragmentShader: CLOUD_FRAG,
    uniforms: {
      uClouds: { value: null },
      uSun: { value: surfMat.uniforms.uSun.value },
      uGlobal: { value: 1 },
    },
    transparent: true,
    depthWrite: false,
  });
  const clouds = new THREE.Mesh(new THREE.SphereGeometry(13.18, 96, 96), cloudMat);
  clouds.renderOrder = 1;
  clouds.visible = false; // the day map has baked clouds; layer disabled to avoid blotching

  const atmoMat = new THREE.ShaderMaterial({
    vertexShader: ATMO_VERT,
    fragmentShader: ATMO_FRAG,
    uniforms: {
      uSun: { value: surfMat.uniforms.uSun.value },
      uGlobal: { value: 1 },
    },
    side: THREE.BackSide,
    blending: THREE.AdditiveBlending,
    transparent: true,
    depthWrite: false,
  });
  const atmo = new THREE.Mesh(new THREE.SphereGeometry(14.3, 96, 96), atmoMat);
  atmo.renderOrder = 2;

  group.add(surface, clouds, atmo);

  const loader = new THREE.TextureLoader();
  loader.setCrossOrigin("anonymous");
  loader.load("/hero/earth_day.jpg", (t) => { t.colorSpace = THREE.SRGBColorSpace; surfMat.uniforms.uDay.value = t; });
  loader.load("/hero/earth_night.png", (t) => { t.colorSpace = THREE.SRGBColorSpace; surfMat.uniforms.uNight.value = t; });
  loader.load("/hero/earth_clouds.png", (t) => { cloudMat.uniforms.uClouds.value = t; });

  const update = (dt) => {
    surface.rotation.y += dt * 0.0085;
    clouds.rotation.y += dt * 0.0125;
  };

  return { group, update, surface, uniforms: surfMat.uniforms };
}

const STAR_VERT = /* glsl */ `
attribute float aSize;
attribute float aPhase;
uniform float uTime;
varying float vTw;
varying float vHue;
void main() {
  vTw = 0.62 + 0.38 * sin(uTime * (0.4 + aPhase * 1.4) + aPhase * 40.0);
  vHue = aPhase;
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mv;
  gl_PointSize = aSize;
}`;

const STAR_FRAG = /* glsl */ `
precision highp float;
uniform float uGlobal;
varying float vTw;
varying float vHue;
void main() {
  float d = length(gl_PointCoord - 0.5);
  float a = smoothstep(0.5, 0.08, d) * vTw * uGlobal;
  vec3 col = mix(vec3(0.82, 0.88, 1.0), vec3(1.0, 0.92, 0.82), step(0.82, vHue));
  gl_FragColor = vec4(col * a, a);
}`;

export function createStars(count = 1500) {
  const pos = new Float32Array(count * 3);
  const size = new Float32Array(count);
  const phase = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    const r = 150 + Math.random() * 240;
    const th = Math.random() * Math.PI * 2;
    const ph = Math.acos(2 * Math.random() - 1);
    pos[i * 3] = r * Math.sin(ph) * Math.cos(th);
    pos[i * 3 + 1] = r * Math.cos(ph);
    pos[i * 3 + 2] = r * Math.sin(ph) * Math.sin(th);
    size[i] = 1.6 + Math.pow(Math.random(), 2.2) * 3.2;
    phase[i] = Math.random();
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  geo.setAttribute("aSize", new THREE.BufferAttribute(size, 1));
  geo.setAttribute("aPhase", new THREE.BufferAttribute(phase, 1));
  const mat = new THREE.ShaderMaterial({
    vertexShader: STAR_VERT,
    fragmentShader: STAR_FRAG,
    uniforms: { uTime: { value: 0 }, uGlobal: { value: 1 } },
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const points = new THREE.Points(geo, mat);
  points.renderOrder = -1;
  points.frustumCulled = false;
  return { points, uniforms: mat.uniforms };
}
